import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { WebPageSchema, BreadcrumbListSchema, ArticleSchema } from '@/components/base/SchemaOrg';
import { allComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = allComics.find((c) => c.id === id);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您要查找的漫画不存在或已被移除</p>
            <Link
              to="/"
              className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = allComics
    .filter((c) => c.id !== comic.id && c.categories.some((cat) => comic.categories.includes(cat)))
    .slice(0, 6);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '分类浏览', to: '/category/all' },
    { label: comic.categories[0], to: `/category/${comic.categories[0]}` },
    { label: comic.title },
  ];

  const categoryLabelMap: Record<string, string> = {
    '恋爱': 'romance', '奇幻': 'fantasy', '动作': 'action', '恐怖': 'horror',
    '搞笑': 'comedy', '剧情': 'drama', '都市': 'urban', '校园': 'school',
    '科幻': 'scifi', '竞技': 'sports', '治愈': 'healing', '悬疑': 'mystery', '穿越': 'timetravel',
  };

  return (
    <>
      <WebPageSchema
        title={`${comic.title}_韩国漫画在线阅读_酷漫屋`}
        description={comic.description}
        url={`https://www.kumanwu.com/comic/${comic.id}`}
        datePublished="2025-01-15"
        dateModified={comic.updatedAt}
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', item: 'https://www.kumanwu.com/' },
          { name: '分类浏览', item: 'https://www.kumanwu.com/category/all' },
          { name: comic.categories[0], item: `https://www.kumanwu.com/category/${categoryLabelMap[comic.categories[0]] || comic.categories[0]}` },
          { name: comic.title, item: `https://www.kumanwu.com/comic/${comic.id}` },
        ]}
      />
      <ArticleSchema
        title={comic.title}
        description={comic.description}
        url={`https://www.kumanwu.com/comic/${comic.id}`}
        imageUrl={comic.cover}
        authorName={comic.author}
        datePublished="2025-01-15"
        dateModified={comic.updatedAt}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <Breadcrumb items={breadcrumbItems} />

          {/* Comic Info */}
          <section className="w-full py-8">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="flex-shrink-0 w-full sm:w-[220px] mx-auto md:mx-0">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画`}
                    title={`${comic.title} - 韩漫在线阅读`}
                    className="w-full aspect-[3/4] object-cover object-top rounded-lg"
                  />
                </div>
                <div className="flex-1">
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                    {comic.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                    <span className="text-foreground-300">|</span>
                    <span className={`text-sm font-medium px-2.5 py-0.5 rounded-full ${
                      comic.status === '连载中' ? 'bg-primary-50 text-primary-600' : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {comic.status}
                    </span>
                    <span className="text-foreground-300">|</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-xs text-accent-500"></i>
                      </div>
                      <span className="text-sm font-medium text-foreground-700">{comic.rating}</span>
                    </div>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(1)}万次` : `${comic.views}次`}阅读
                    </span>
                    <span className="text-foreground-300">|</span>
                    <span className="text-sm text-foreground-600">共 {comic.chapters.length} 话</span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-5">
                    {comic.categories.map((cat) => (
                      <Link
                        key={cat}
                        to={`/category/${cat}`}
                        className="px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap"
                      >
                        {cat}
                      </Link>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-700 leading-relaxed mb-6 max-w-2xl">
                    {comic.description}
                  </p>

                  <div className="flex items-center gap-3">
                    {comic.chapters.length > 0 && (
                      <a
                        href={`#chapter-1`}
                        className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap"
                      >
                        开始阅读
                      </a>
                    )}
                    <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-200 transition-all">
                      <i className="ri-heart-line text-lg"></i>
                    </button>
                    <button className="w-10 h-10 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:text-primary-500 hover:border-primary-200 transition-all">
                      <i className="ri-share-forward-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Chapter List */}
          <section className="w-full py-8 bg-background-100">
            <div className="max-w-4xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">章节列表</h2>
                <div className="flex items-center gap-2">
                  <button className="px-3 py-1.5 rounded text-xs font-medium bg-background-50 border border-background-200 text-foreground-600 hover:bg-background-200 transition-all">
                    正序
                  </button>
                  <button className="px-3 py-1.5 rounded text-xs font-medium bg-primary-50 text-primary-600 border border-primary-200 whitespace-nowrap">
                    倒序
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {comic.chapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    id={`chapter-${chapter.number}`}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/20 transition-all cursor-pointer"
                  >
                    <span className="text-sm text-foreground-800 line-clamp-1">
                      第{chapter.number}话 {chapter.title !== `第${chapter.number}话` ? chapter.title : ''}
                    </span>
                    <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{chapter.updatedAt}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Related Comics */}
          {relatedComics.length > 0 && (
            <section className="w-full py-14">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-6">
                  相关推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                  {relatedComics.map((c) => (
                    <ComicCard key={c.id} comic={c} />
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}